import { FormInput } from '@/components/FormInput';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useUpdateProfileMutation, useProfileQuery } from '@/models/user';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

type FormType = {
  email: string;
  companyName: string;
  tin: string;
  phoneNumber: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
};

export const ProfileDetailsSection = () => {
  const { data: profileData } = useProfileQuery();
  const { t } = useTranslation();
  const [
    updateProfile,
    { isLoading: isUpdateProfileLoading, isSuccess: isUpdateProfileSuccess },
  ] = useUpdateProfileMutation();

  const validationSchema = yup
    .object({
      tin: yup.string().required(t('validation.empty')),
      companyName: yup.string().required(t('validation.empty')),
      phoneNumber: yup
        .string()
        .required(t('validation.empty'))
        .matches(
          /^\+[0-9]+$/,
          t('profile_details_section.form.phone_number_required_format')
        ),
      address: yup.string().required(t('validation.empty')),
      postalCode: yup.string().required(t('validation.empty')),
      city: yup.string().required(t('validation.empty')),
      country: yup.string().required(t('validation.empty')),
    })
    .required();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormType>({
    mode: 'onSubmit',
    // @ts-ignore
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: profileData?.email,
      companyName: profileData?.companyName,
      tin: profileData?.tin,
      phoneNumber: profileData?.phoneNumber,
      address: profileData?.address,
      postalCode: profileData?.postalCode,
      city: profileData?.city,
      country: profileData?.country,
    },
  });

  const onSubmit = async (data: FormType) => {
    await updateProfile({
      tin: data?.tin,
      address: data?.address,
      city: data.city,
      country: data.country,
      phoneNumber: data.phoneNumber,
      postalCode: data.postalCode,
      companyName: data.companyName,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormInput
        name="email"
        label={t('profile_details_section.form.email')}
        control={control}
        error={errors.email}
        variant="outlined"
        fullWidth
        disabled
      />

      <FormInput
        name="companyName"
        label={t('profile_details_section.form.company_name')}
        control={control}
        error={errors.companyName}
        variant="outlined"
        fullWidth
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        <FormInput
          name="tin"
          label={t('profile_details_section.form.tin')}
          control={control}
          error={errors.tin}
          variant="outlined"
          fullWidth
        />
        <FormInput
          name="phoneNumber"
          label={t('profile_details_section.form.phone_number')}
          control={control}
          error={errors.phoneNumber}
          variant="outlined"
          fullWidth
        />
      </div>

      <FormInput
        name="address"
        label={t('profile_details_section.form.address')}
        control={control}
        error={errors.address}
        variant="outlined"
        fullWidth
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        <FormInput
          name="postalCode"
          label={t('profile_details_section.form.postalCode')}
          control={control}
          error={errors.postalCode}
          variant="outlined"
          fullWidth
        />
        <FormInput
          name="city"
          label={t('profile_details_section.form.city')}
          control={control}
          error={errors.city}
          variant="outlined"
          fullWidth
        />
      </div>

      <FormInput
        name="country"
        label={t('profile_details_section.form.country')}
        control={control}
        error={errors.country}
        variant="outlined"
        fullWidth
      />

      <div className="mt-5">
        <LoadingButton
          loading={isUpdateProfileLoading}
          type="submit"
          variant="contained"
          color={isUpdateProfileSuccess ? 'success' : 'secondary'}
          className="w-full"
          disabled={isUpdateProfileLoading}
          startIcon={isUpdateProfileSuccess ? <CheckCircleOutlineIcon /> : null}
        >
          {t('profile_details_section.form.save')}
        </LoadingButton>
      </div>
    </form>
  );
};
